'use client';

import { getProduct, products } from '@/lib/products';
import { useLocale } from '@/lib/i18n/LocaleContext';
import { pageCopy } from '@/lib/i18n/pageCopy';
import { dilaCrmCopy } from '@/lib/i18n/pages/dilacrm';
import { productsCommonCopy } from '@/lib/i18n/pages/productsCommon';
import { BrowserShot, Tilt } from './shots';
import { Reveal } from './Reveal';
import { ProductHero } from './hero';
import {
  AccentGlow,
  DemoBand,
  FeatureRows,
  FloatChip,
  NextProducts,
  ProductMarquee,
  SectionHead,
  StatGrid,
} from './blocks';

const product = getProduct('dilacrm')!;
const A = product.accent;
const IMG = '/images/products/dilacrm';

/** Real numbers, identical in every locale; labels come from the copy file. */
const CRM_STATS = ['06', '05', '03', '24/7'];

export function DilaCrmPage() {
  const { locale, href } = useLocale();
  const t = pageCopy(dilaCrmCopy, locale);
  const c = pageCopy(productsCommonCopy, locale);
  const cursor = { goLabel: c.cursorGo, openLabel: c.cursorOpen };
  return (
    <div className="self-styled relative text-white">
      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <ProductHero
        name="DilaCRM"
        accent={A}
        title={t.hero.title}
        lede={t.hero.lede}
        secondary={{ label: t.hero.secondary, href: '#inbox' }}
        liveHref={product.liveUrl}
        liveLabel={
          <>
            {t.hero.liveLabel} · <bdi dir="ltr" className="keep-latin">{product.domain}</bdi>
          </>
        }
        breadcrumbLabel={c.breadcrumbProducts}
        productsHref={href('/products')}
        bookLabel={c.bookDemo}
        bookHref={href('/contact')}
        {...cursor}
      >
        <AccentGlow accent={A} className="start-1/4 top-1/4 h-80 w-80" />
        <div className="relative rotate-1">
          <Tilt>
            <BrowserShot
              src={`${IMG}/hero-approval.webp`}
              alt={t.hero.imageAlt}
              url={product.domain}
              accent={A}
              priority
              sizes="(max-width: 1024px) 100vw, 55vw"
            />
          </Tilt>
        </div>
        <FloatChip accent={A} className="-start-4 top-10" dot>
          {t.hero.chipDraft}
        </FloatChip>
        <FloatChip accent={A} className="-end-3 top-1/3" alt>
          WhatsApp · Instagram
        </FloatChip>
        <FloatChip accent={A} className="bottom-8 start-1/4">
          Telegram · TikTok · {t.hero.chipEmail}
        </FloatChip>
      </ProductHero>

      {/* ── The one marquee: channels + languages ────────────────────── */}
      <ProductMarquee accent={A} items={t.marquee} />

      {/* ── The inbox (split: copy left, shot right) ─────────────────── */}
      <section id="inbox" aria-label={t.inbox.aria} className="px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto grid max-w-[1400px] items-center gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <SectionHead
              accent={A}
              title={t.inbox.title}
              lede={t.inbox.lede}
            />
            <FeatureRows accent={A} className="mt-10" rows={t.inbox.rows} />
          </Reveal>
          <Reveal className="relative lg:col-span-7" delay={120}>
            <AccentGlow accent={A} className="-end-8 bottom-0 h-64 w-64" opacity={0.16} />
            <BrowserShot
              src={`${IMG}/inbox.webp`}
              alt={t.inbox.shotAlt}
              url={`${product.domain}/inbox`}
              accent={A}
              sizes="(max-width: 1024px) 100vw, 55vw"
            />
          </Reveal>
        </div>
      </section>

      {/* ── Human in the loop (centered stack) ───────────────────────── */}
      <section aria-label={t.loop.aria} className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <SectionHead
              kicker={t.loop.kicker}
              accent={A}
              title={t.loop.title}
              lede={t.loop.lede}
              className="max-w-4xl"
            />
          </Reveal>
          <Reveal delay={120}>
            <FeatureRows accent={A} className="mt-14" rows={t.loop.rows} />
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-10 font-mono text-[11px] uppercase tracking-[0.2em] rtl:tracking-normal text-white/40">
              {t.loop.note}
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── Comments (split: shot left, copy right) ──────────────────── */}
      <section aria-label={t.comments.aria} className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto grid max-w-[1400px] items-center gap-14 lg:grid-cols-12">
          <Reveal className="relative order-2 lg:order-1 lg:col-span-7" delay={120}>
            <AccentGlow accent={A} className="-start-8 top-10 h-64 w-64" opacity={0.16} />
            <BrowserShot
              src={`${IMG}/comments-ku.webp`}
              alt={t.comments.shotAlt}
              url={`${product.domain}/comments`}
              accent={A}
              sizes="(max-width: 1024px) 100vw, 55vw"
            />
          </Reveal>
          <Reveal className="order-1 lg:order-2 lg:col-span-5">
            <SectionHead
              accent={A}
              title={t.comments.title}
              lede={t.comments.lede}
            />
            <FeatureRows accent={A} className="mt-10" rows={t.comments.rows} />
          </Reveal>
        </div>
      </section>

      {/* ── Campaigns + ads (asymmetric bento) ───────────────────────── */}
      <section aria-label={t.campaigns.aria} className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto grid max-w-[1400px] gap-6 lg:grid-cols-12">
          <Reveal className="flex flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-7 md:p-9 lg:col-span-5">
            <h3 className="rtl-display-m font-six-caps text-5xl lowercase leading-[0.9] text-white md:text-6xl">
              {t.campaigns.title}
            </h3>
            <p className="mt-5 max-w-md text-sm font-light leading-relaxed text-white/55 rtl:text-base md:text-base">
              {t.campaigns.body}
            </p>
            <div className="mt-8 flex-1">
              <BrowserShot
                src={`${IMG}/campaigns.webp`}
                alt={t.campaigns.shotAlt}
                url={`${product.domain}/campaigns`}
                accent={A}
                aspect="aspect-[4/3]"
                sizes="(max-width: 1024px) 100vw, 38vw"
              />
            </div>
          </Reveal>
          <Reveal
            className="flex flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-7 md:p-9 lg:col-span-7"
            delay={120}
          >
            <h3 className="rtl-display-m font-six-caps text-5xl lowercase leading-[0.9] text-white md:text-6xl">
              {t.campaigns.adsTitle}
            </h3>
            <p className="mt-5 max-w-md text-sm font-light leading-relaxed text-white/55 rtl:text-base md:text-base">
              {t.campaigns.adsBody}
            </p>
            <div className="mt-8 flex-1">
              <BrowserShot
                src={`${IMG}/ads.webp`}
                alt={t.campaigns.adsAlt}
                url={`${product.domain}/social-ads`}
                accent={A}
                aspect="aspect-[16/9]"
                sizes="(max-width: 1024px) 100vw, 55vw"
              />
            </div>
            <ul className="mt-7 flex flex-wrap gap-2.5" aria-label={t.campaigns.metricsAria}>
              {t.campaigns.metrics.map((metric) => (
                <li
                  key={metric}
                  className="rounded-full border border-white/12 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] rtl:tracking-normal text-white/60"
                >
                  {metric}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* ── CRM (full-width band) ────────────────────────────────────── */}
      <section aria-label={t.crm.aria} className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <SectionHead
              accent={A}
              title={t.crm.title}
              lede={t.crm.lede}
            />
          </Reveal>
          <Reveal delay={120} className="relative mt-14">
            <AccentGlow accent={A} className="end-1/4 -top-8 h-64 w-64" opacity={0.16} />
            <BrowserShot
              src={`${IMG}/pipeline.webp`}
              alt={t.crm.shotAlt}
              url={`${product.domain}/crm`}
              accent={A}
              aspect="aspect-[16/9]"
              sizes="(max-width: 1024px) 100vw, 85vw"
            />
          </Reveal>
          <Reveal delay={180}>
            <FeatureRows accent={A} className="mt-14" rows={t.crm.rows} />
          </Reveal>
        </div>
      </section>

      {/* ── Stats ────────────────────────────────────────────────────── */}
      <section aria-label={t.stats.aria} className="border-t border-white/10 px-6 py-24 md:px-12 md:py-28 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <StatGrid
              accent={A}
              stats={CRM_STATS.map((value, i) => ({ value, label: t.stats.labels[i] }))}
            />
          </Reveal>
        </div>
      </section>

      {/* ── Language band (duo shots) ────────────────────────────────── */}
      <section aria-label={t.languages.aria} className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <SectionHead
              accent={A}
              title={t.languages.title}
              lede={t.languages.lede}
              className="max-w-4xl"
            />
          </Reveal>
          <div className="mt-14 grid gap-6 lg:grid-cols-12">
            <Reveal className="lg:col-span-7" delay={100}>
              <BrowserShot
                src={`${IMG}/rtl-ar.webp`}
                alt={t.languages.rtlAlt}
                url={product.domain}
                accent={A}
                aspect="aspect-[16/10]"
                sizes="(max-width: 1024px) 100vw, 55vw"
              />
              <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] rtl:tracking-normal text-white/45">
                {t.languages.rtlCaption}
              </p>
            </Reveal>
            <Reveal className="lg:col-span-5 lg:pt-16" delay={200}>
              <BrowserShot
                src={`${IMG}/channels.webp`}
                alt={t.languages.channelsAlt}
                url={`${product.domain}/channels`}
                accent={A}
                aspect="aspect-[4/3]"
                sizes="(max-width: 1024px) 100vw, 38vw"
              />
              <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] rtl:tracking-normal text-white/45">
                {t.languages.channelsCaption}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Demo + cross-sell ────────────────────────────────────────── */}
      <div className="px-6 md:px-12 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <DemoBand
            ghost="DilaCRM"
            title={t.demo.title}
            sub={t.demo.sub}
            primary={{ label: c.bookDemo, href: href('/contact') }}
            secondary={{ label: t.demo.secondary, href: product.liveUrl, external: true }}
            accent={A}
            eyebrow={c.bookDemo}
            note={
              <>
                {c.liveLabel}: <bdi dir="ltr" className="keep-latin">{product.domain}</bdi>
              </>
            }
            {...cursor}
          />
          <NextProducts
            className="mb-24"
            ariaLabel={c.otherProductsAria}
            viewLabel={c.cursorView}
            href={href}
            items={products
              .filter((p) => p.slug !== 'dilacrm')
              .map((p) => ({
                slug: p.slug,
                name: p.name,
                tagline: c.taglines[p.slug],
                accent: p.accent,
              }))}
          />
        </div>
      </div>
    </div>
  );
}
